import { Notification } from '@vae/server/dist/client'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../analytics/utils/firebaseUtils'
import useUser from '../../contexts/hooks/useUser'
import { trpc } from '../../modules/trpc'
import { getInterventionIdsFromList } from '../../utils/getInterventionIdsWithoutDuplicates'
import PageWrapper from '../../wrappers/pageWrapper/PageWrapper'
import DisplayNotifications from './components/DisplayNotifications'
import { NotificationGroupAndDate } from './components/NotificationGroupAndDate'
import VehicleLinkAlert from './components/VehicleLinkAlert'
import { groupNotificationsByAge } from './components/groupNotificationsByAge'

const Notifications = () => {
	const { user } = useUser()

	const { t } = useTranslation('pages_notifications')

	const { data: notifications } = trpc.user.notifications.get.useQuery(
		{ userId: user?.id ?? 0 },
		{ initialData: [], enabled: !!user?.id, staleTime: 1000 * 60 } // Refetch notifications every minute
	)

	const { data: vehicles } = trpc.eNode.vehicles.get.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
		},
		{ enabled: !!user?.hashedPid }
	)

	const interventionList = getInterventionIdsFromList(vehicles ?? [])

	const readAllNotificationsMutation = trpc.user.notifications.read.useMutation({
		onSuccess: () => {
			logEvent('auto-read-notifications')
		},
	})

	useEffect(() => {
		if (user?.id) {
			readAllNotificationsMutation.mutate({ userId: user?.id })
		}

		return
	}, [])

	const groupedNotifications = useMemo(
		() => groupNotificationsByAge(notifications),
		[notifications]
	)

	const renderNotifications = (isNew: boolean) => {
		const filteredGroupedNotifications = Object.entries(groupedNotifications).filter(
			([, notifications]) =>
				notifications &&
				notifications.some((notification: Notification) => notification.isRead !== isNew)
		)

		if (filteredGroupedNotifications.length === 0) {
			return (
				<p className='no-notis text-text-grey-200 dark:text-white'>
					{isNew ? t('eiUusia') : t('eiVanhoja')}
				</p>
			)
		}

		return filteredGroupedNotifications.map(([group, notifications]) => {
			const filteredNotifications = notifications.filter(
				(notification: Notification) => notification.isRead !== isNew
			)
			if (filteredNotifications.length === 0) return null

			return (
				<div key={group}>
					<NotificationGroupAndDate group={group} />
					<DisplayNotifications
						notifications={filteredNotifications}
						isNewNotifications={isNew}
						language={user?.language ?? 'fi'}
					/>
				</div>
			)
		})
	}

	return (
		<PageWrapper page='notifications'>
			{!user ? (
				<p className='text-center text-text-grey-200 dark:text-white'>
					{t('userlessNotifications')}
				</p>
			) : (
				<div className='outer bg-white dark:bg-brand-secondary-200-dark'>
					{interventionList.length > 0 && <VehicleLinkAlert />}
					<div className='profile-filling !pt-0'>
						<div className='inner dark:bg-brand-primary-200-dark'>
							<p className='text-brand-primary-100-light dark:text-brand-primary-100-dark'>
								{t('uudet')}
							</p>
							{renderNotifications(true)}
							<div className='border-t border-brand-secondary-200-light dark:border-brand-secondary-100-dark' />
							<p className='text-brand-primary-100-light dark:text-brand-primary-100-dark mt-4'>
								{t('vanhat')}
							</p>
							{renderNotifications(false)}
						</div>
					</div>
				</div>
			)}
		</PageWrapper>
	)
}

export default Notifications
